// ePOS-Print XML builder for Epson TM printers (TM-m30III etc.) via the
// free "Epson TM Print Assistant" iOS/Android app — prints straight to a
// Bluetooth/LAN Epson printer with NO system print dialog (Shopify-POS style).

const xmlEsc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const width = (store) => (store?.paper_width === '58' ? 32 : 48);

const padLine = (left, right, w) => {
  left = String(left ?? '');
  right = String(right ?? '');
  if (left.length + right.length + 1 > w) left = left.slice(0, Math.max(0, w - right.length - 1));
  return left + ' '.repeat(Math.max(1, w - left.length - right.length)) + right;
};

const t = (text, { align, em, dw, dh } = {}) =>
  `<text align="${align || 'left'}" em="${em ? 'true' : 'false'}" dw="${dw ? 'true' : 'false'}" dh="${dh ? 'true' : 'false'}">${xmlEsc(text)}&#10;</text>`;

export const fmtReceiptDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hourCycle: 'h12',
    });
  } catch { return (iso || '').slice(0, 16).replace('T', ' '); }
};

export const payLabel = (p) => {
  if (typeof p === 'object' && p) {
    if (p.label) return p.label;
    p = p.method;
  }
  return { cash: 'Cash', card: 'Card', link: 'Payment link', store_credit: 'Store credit' }[p] || (p || '').replace(/^\w/, (c) => c.toUpperCase());
};

export const buildReceiptXml = (receipt) => {
  const { order, store } = receipt;
  const cur = receipt.currency ? `${receipt.currency} ` : '';
  const money = (n) => `${cur}${Number(n || 0).toFixed(2)}`;
  const w = width(store);
  const div = '-'.repeat(w);
  const parts = ['<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">'];
  // Drawer kick FIRST — some firmwares ignore a pulse placed after <cut>.
  let hasCash = false;
  for (const p of order.pos?.payments || []) {
    if (p.method === 'cash') hasCash = true;
  }
  if (hasCash) parts.push('<pulse drawer="drawer_1" time="pulse_100"/>');
  // Header
  parts.push(t(store?.name || receipt.company_name || '', { align: 'center', em: true }));
  if (store?.address) parts.push(t(store.address, { align: 'center' }));
  if (store?.phone) parts.push(t(store.phone, { align: 'center' }));
  if (store?.trn) parts.push(t(`TRN: ${store.trn}`, { align: 'center' }));
  if (store?.receipt_header) parts.push(t(store.receipt_header, { align: 'center', em: true }));
  // Big total block
  parts.push(t(''));
  parts.push(t('TOTAL', { align: 'center' }));
  parts.push(t(money(order.total_amount), { align: 'center', em: true, dw: true, dh: true }));
  parts.push(t(''));
  // Items
  parts.push(t(padLine('Items', 'Price', w), { em: true }));
  parts.push(t(div));
  for (const i of order.items || []) {
    const line = Number(i.quantity) * Number(i.price);
    const nm = String(i.name || i.sku || '');
    if (Number(i.quantity) === 1 && !(i.discount > 0)) {
      const price = money(line);
      parts.push(t(padLine(nm.slice(0, w - price.length - 1), price, w)));
    } else {
      parts.push(t(nm.slice(0, w)));
      parts.push(t(padLine(`  ${i.quantity} x ${money(i.price)}`, money(line), w)));
      if (i.discount > 0) parts.push(t(padLine('  Discount', `-${money(i.discount)}`, w)));
    }
    if (i.note) parts.push(t(`  * ${i.note}`.slice(0, w)));
  }
  parts.push(t(div));
  // Totals
  parts.push(t(padLine('SUBTOTAL', money(order.subtotal), w)));
  if (order.discount_amount > 0) parts.push(t(padLine('CART DISCOUNT', `-${money(order.discount_amount)}`, w)));
  parts.push(t(padLine('TOTAL', money(order.total_amount), w), { em: true }));
  parts.push(t(div));
  // Payments — method (custom label + reference) / tendered / change
  for (const p of order.pos?.payments || []) {
    parts.push(t(padLine(payLabel(p) + (p.reference ? ` (${String(p.reference).slice(0, 14)})` : ''), money(p.amount), w)));
  }
  if (order.pos?.change_given > 0) parts.push(t(padLine('Change due', money(order.pos.change_given), w), { em: true }));
  parts.push(t(div));
  // Order reference (no "Shopify" word) + delivery address
  if (order.shopify_order_name) parts.push(t(`Order: ${order.shopify_order_name}`));
  const a = order.shipping_address || {};
  if (a.address1) {
    parts.push(t('Ship to:', { em: true }));
    const full = `${a.first_name || ''} ${a.last_name || ''}`.trim();
    if (full) parts.push(t(full));
    parts.push(t(a.address1));
    if (a.address2) parts.push(t(a.address2));
    const cityLine = [a.city, a.province, a.zip].filter(Boolean).join(', ');
    if (cityLine) parts.push(t(cityLine));
    if (a.country) parts.push(t(a.country));
    if (a.phone) parts.push(t(a.phone));
  } else if (store?.show_customer_on_receipt !== false && order.customer_name && order.customer_name !== 'Walk-in Customer') {
    parts.push(t(`Customer: ${order.customer_name}`));
  }
  parts.push(t(div));
  // Footer block
  parts.push(t(fmtReceiptDate(order.created_at), { align: 'center' }));
  parts.push(t(`Staff at register: ${order.pos?.staff_name || ''}`, { align: 'center' }));
  parts.push(t(`Receipt: ${order.order_number}`, { align: 'center' }));
  if (store?.receipt_footer) parts.push(t(store.receipt_footer, { align: 'center' }));
  parts.push('<feed line="3"/>');
  parts.push('<cut type="feed"/>');
  parts.push('</epos-print>');
  return parts.join('');
};

export const buildKotXml = (receipt) => {
  const { order, store } = receipt;
  const w = width(store);
  const div = '-'.repeat(w);
  const type = order.pos?.order_type === 'takeaway' ? 'TAKEAWAY'
    : order.pos?.order_type === 'delivery' ? 'DELIVERY'
    : `TABLE ${order.pos?.table_number || '-'}`;
  const parts = ['<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">'];
  parts.push(t('*** KITCHEN ***', { align: 'center', em: true }));
  parts.push(t(type, { align: 'center', em: true }));
  parts.push(t(`${order.order_number} · ${(order.created_at || '').slice(11, 16)}`));
  parts.push(t(div));
  for (const i of order.items || []) {
    parts.push(t(`${i.quantity} x ${i.name || i.sku || ''}`, { em: true }));
    if (i.note) parts.push(t(`  >> ${i.note}`));
  }
  parts.push(t(div));
  parts.push(t(`Staff: ${order.pos?.staff_name || ''}`));
  parts.push('<feed line="3"/>');
  parts.push('<cut type="feed"/>');
  parts.push('</epos-print>');
  return parts.join('');
};

// Opens the Epson TM Print Assistant app which prints directly to the
// paired Bluetooth/LAN printer and returns. No iOS print dialog.
// onNoApp fires when the app doesn't seem to be installed (page never hid).
export const epsonPrint = (xml, onNoApp) => {
  const url =
    `tmprintassistant://tmprintassistant.epson.com/print?ver=1&data-type=eposprintxml&reselect=yes&data=${encodeURIComponent(xml)}`;
  let left = false;
  const onHide = () => { if (document.visibilityState === 'hidden') left = true; };
  document.addEventListener('visibilitychange', onHide);
  window.location.href = url;
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    if (!left && document.visibilityState === 'visible' && onNoApp) onNoApp();
  }, 2500);
};

export const buildTestXml = (store) => {
  const w = width(store);
  return '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">'
    + t(store?.name || 'Opswarex POS', { align: 'center', em: true })
    + t('Printer test — OK', { align: 'center' })
    + t('-'.repeat(w))
    + t(new Date().toISOString().slice(0, 16).replace('T', ' '), { align: 'center' })
    + '<feed line="3"/><cut type="feed"/></epos-print>';
};

export const noAppToastArgs = () => ([
  'Epson TM Print Assistant app not found',
  {
    description: 'On this iPad: install the free "Epson TM Print Assistant" app from the App Store, open it once and pair your TM-m30III over Bluetooth, then print again.',
    duration: 12000,
  },
]);

export const usesEpsonApp = (store) => store?.print_method === 'epson_tmassistant';
